import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Mail } from 'lucide-react'
import { Layout } from '@/components/layout'

export function SecurityPolicyPage() {
  return (
    <Layout>
      <div className="border-b bg-muted/30">
        <div className="container mx-auto px-6 py-12">
          <h1 className="text-4xl font-bold tracking-tight text-foreground mb-3">Beveiligingsbeleid</h1>
          <p className="text-xl text-muted-foreground leading-relaxed">
            BGCH Crisis Communication Buddy
          </p>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-3xl space-y-8">
          {/* Section 1: Introduction */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              1. Inleiding
            </h2>
            <p className="text-foreground mb-4">
              De BGCH Crisiscommunicatie Buddy (hierna "Crisis Buddy") is een AI-aangedreven webtoepassing
              die ontworpen is om Nederlandse gemeentelijke crisiscommunicatieteams te ondersteunen. Wij nemen veiligheid
              serieus en zijn toegewijd aan het handhaven van de hoogste normen voor veiligheid, betrouwbaarheid en naleving.
            </p>
            <p className="text-foreground">
              Dit beveiligingsbeleid beschrijft onze aanpak van kwetsbaarheidsmanagement en hoe we rapportages over veiligheid
              van de community afhandelen.
            </p>
          </section>

          {/* Section 2: Scope */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              2. Werkingsgebied
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Dit beleid is van toepassing op:</h3>
                <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                  <li>
                    De belangrijkste Crisis Buddy-toepassing die wordt gehost op <strong>crisisbuddy.nl</strong> en{' '}
                    <strong>www.crisisbuddy.nl</strong>
                  </li>
                  <li>Alle bijbehorende API's en backend-services</li>
                  <li>Integraties met derden (Mistral AI, Coosto, SharePoint) voor zover deze betrekking hebben op Crisis Buddy</li>
                </ul>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-2">Dit beleid is niet van toepassing op:</h3>
                <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                  <li>Services, websites of toepassingen van derden die vanuit Crisis Buddy worden gelinkt</li>
                  <li>Kwetsbaarheden in afhankelijkheden of upstream-systemen (rapporteer deze aan de respectieve beheerders)</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3: Reporting Security Vulnerabilities */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              3. Rapportage van beveiligingskwetsbaarheden
            </h2>
            <p className="text-foreground mb-4">
              We moedigen verantwoorde openbaarmaking van beveiligingskwetsbaarheden aan. Als u een beveiligingskwetsbaarheid
              in Crisis Buddy ontdekt, rapporteer deze dan onmiddellijk aan ons in plaats van deze openbaar te maken.
            </p>

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">3.1 Hoe te rapporteren</h3>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-3 mb-4">
                      <Mail className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-semibold text-foreground">E-mailadres:</p>
                        <a href="mailto:security@techletes.ai" className="text-primary hover:underline font-semibold">
                          security@techletes.ai
                        </a>
                      </div>
                    </div>
                    <p className="font-semibold text-foreground mb-2">Voeg de volgende informatie in uw rapport op:</p>
                    <ul className="list-disc list-inside space-y-1 text-foreground ml-2">
                      <li>Gedetailleerde beschrijving van de kwetsbaarheid</li>
                      <li>Stappen om het probleem te reproduceren</li>
                      <li>Betrokken componenten of eindpunten</li>
                      <li>Mogelijke gevolgen (vertrouwelijkheid, integriteit, beschikbaarheid)</li>
                      <li>Proof of concept of schermafbeelding (indien van toepassing)</li>
                      <li>Uw contactgegevens</li>
                    </ul>
                  </CardContent>
                </Card>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">3.2 Reactietijdlijn</h3>
                <p className="text-foreground mb-3">We streven ernaar om:</p>
                <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                  <li>
                    <strong>Ontvangst te bevestigen</strong> van uw rapport binnen 24-48 uur
                  </li>
                  <li>
                    <strong>Een eerste beoordeling te geven</strong> binnen 5-7 werkdagen
                  </li>
                  <li>
                    <strong>Een oplossing te ontwikkelen en te testen</strong> binnen 14-30 dagen (afhankelijk van ernst)
                  </li>
                  <li>
                    <strong>Een patch uit te brengen</strong> en u op de hoogte te stellen van de implementatie
                  </li>
                  <li>
                    <strong>Openbaarmaking van gegevens coördineren</strong> nadat is geverifieerd dat gebruikers zijn beschermd
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-foreground mb-3">3.3 Ernst van kwetsbaarheden</h3>
                <p className="text-foreground mb-3">We classificeren kwetsbaarheden op basis van de CVSS v3.1-norm:</p>
                <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                  <li>
                    <strong>Kritiek (9.0-10.0):</strong> Onmiddellijke patching en implementatie
                  </li>
                  <li>
                    <strong>Hoog (7.0-8.9):</strong> Patching binnen 7 dagen
                  </li>
                  <li>
                    <strong>Gemiddeld (4.0-6.9):</strong> Patching binnen 30 dagen
                  </li>
                  <li>
                    <strong>Laag (0.1-3.9):</strong> Opgenomen in reguliere releasecycli
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4: What We Will Do */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              4. Wat we zullen doen
            </h2>
            <p className="text-foreground mb-3">Na ontvangst van een beveiligingsrapport zullen we:</p>
            <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
              <li>Uw rapport bevestigen en een referentienummer toewijzen</li>
              <li>U op de hoogte houden van de voortgang richting een oplossing</li>
              <li>Krediet geven aan de rapporteur (indien gewenst) in beveiligingsadviezen</li>
              <li>Met u samenwerken om ervoor te zorgen dat de kwetsbaarheid goed wordt begrepen en opgelost</li>
              <li>Het moment van openbaarmaking met u coördineren</li>
            </ul>
          </section>

          {/* Section 5: What We Ask */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              5. Wat vragen we
            </h2>
            <p className="text-foreground mb-3">Om ons effectief te laten reageren, vragen we u het volgende:</p>
            <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
              <li>
                <strong>Openbaar niet</strong> de kwetsbaarheid openbaar of aan derden totdat we een patch hebben uitgebracht
              </li>
              <li>
                <strong>Toegang niet tot</strong> gebruikersgegevens, systemen of andere informatie verder dan nodig is
                om de kwetsbaarheid aan te tonen
              </li>
              <li>
                <strong>Voer niet uit</strong> denial-of-service-aanvallen of ander destructief testen
              </li>
              <li>
                <strong>Geef redelijke tijd</strong> om een patch te ontwikkelen en uit te voeren vóór openbare openbaarmaking
              </li>
              <li>
                <strong>Wees respectvol</strong> en professioneel in alle communicatie
              </li>
            </ul>
          </section>

          {/* Section 6: Security Measures */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              6. Beveiligingsmaatregelen
            </h2>
            <p className="text-foreground mb-4">Crisis Buddy implementeert meerdere beveiligingsmaatregelen om gebruikersgegevens en systeemintegriteit te beschermen:</p>

            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">6.1 Toepassingsbeveiliging</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                    <li>Content Security Policy (CSP) om XSS-aanvallen te voorkomen</li>
                    <li>HTTPS/TLS-codering voor alle gegevens in transit</li>
                    <li>CSRF-beveiliging op alle statuswijzigende bewerkingen</li>
                    <li>Invoervalidatie en uitvoercodering</li>
                    <li>Preventie van SQL-injectie via geparametriseerde query's</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">6.2 Authenticatie & Autorisatie</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                    <li>JWT-gebaseerde authenticatie met veilig tokenbeheer</li>
                    <li>Op rollen gebaseerde toegangscontrole (RBAC)</li>
                    <li>Sessiebeheer met veilige cookie-flags</li>
                    <li>Bescherming tegen veelvoorkomende authenticatieaanvallen (brute force, sessievastlegging)</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">6.3 Gegevensbescherming</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                    <li>GDPR-compliance en gegevensbeperkingen</li>
                    <li>Geen persoonlijk identificeerbare informatie (PII) in AI-gegenereerde outputs</li>
                    <li>Veilige gegevensopslag met passende versleuteling</li>
                    <li>Regelmatige beveiligingsaudits en penetratietests</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">6.4 Infrastructuur</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
                    <li>Veilige implementatiepijplijn met geautomatiseerd testen</li>
                    <li>Snelheidsbeperkingen en DDoS-bescherming</li>
                    <li>Beveiligingsheaders (HSTS, X-Frame-Options, enz.)</li>
                    <li>Regelmatige updates en patchbeheer van afhankelijkheden</li>
                    <li>Logboekregistratie en monitoring van beveiligingsgebeurtenissen</li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          {/* Section 7: Known Limitations */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              7. Bekende beperkingen
            </h2>
            <p className="text-foreground mb-3">We erkennen de volgende beperkingen in onze huidige beveiligingspositie:</p>
            <ul className="list-disc list-inside space-y-2 text-foreground ml-2">
              <li>
                <strong>Afhankelijkheidskwetsbaarheden:</strong> Hoewel we afhankelijkheden regelmatig bijwerken, kunnen
                zero-day-kwetsbaarheden in pakketten van derden bestaan
              </li>
              <li>
                <strong>Social engineering:</strong> Geen beveiligingssysteem kan alle social engineering-aanvallen op
                gebruikers voorkomen
              </li>
              <li>
                <strong>Aanvallen in de toeleveringsketen:</strong> We vertrouwen op services van derden (Mistral AI, Coosto,
                SharePoint) die hun eigen kwetsbaarheden kunnen hebben
              </li>
            </ul>
          </section>

          {/* Section 8: Contact */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              8. Contact
            </h2>
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Beveiligingscontact
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-foreground">
                  <strong>E-mailadres:</strong>{' '}
                  <a href="mailto:security@techletes.ai" className="text-primary hover:underline">
                    security@techletes.ai
                  </a>
                </p>
                <p className="text-foreground">
                  <strong>Reactietijd:</strong> Binnen 24-48 uur voor beveiligingsrapportages
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Section 9: Disclaimer */}
          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4 pb-3 border-l-4 border-primary pl-4">
              9. Disclaimer
            </h2>
            <p className="text-foreground">
              Hoewel we ernaar streven de hoogste beveiligingsnormen te handhaven, is geen enkel systeem volledig veilig.
              We bieden dit beleid om duidelijke communicatiekanalen en verwachtingen vast te stellen voor het omgaan met
              beveiligingskwetsbaarheden. Dit beleid schept geen juridische verplichtingen voorbij die al vastgesteld zijn
              door toepasselijk recht.
            </p>
          </section>

          {/* Footer */}
          <div className="mt-12 pt-8 border-t border-border">
            <p className="text-sm text-muted-foreground">
              <strong>Laatst bijgewerkt:</strong> 3 december 2025
            </p>
            <p className="text-sm text-muted-foreground mt-2">
              Dit beveiligingsbeleid kan op elk moment worden bijgewerkt. Wijzigingen zullen op deze pagina worden weergegeven.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  )
}
